import { Col, Row, Tooltip } from "antd-v5";
import { FormInstance } from "antd/lib/form";
import classNames from "classnames";
import CustomSelect from "components/CustomSelect";
import { InfoIcon, PlusIcon, RightArrowIcon } from "components/Icons";
import { useFeatureFlag } from "contexts/FeatureFlagContext";
import _, { isEmpty } from "lodash";
import React, {
  MutableRefObject,
  ReactElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Rule } from "turndown";
import { getField } from "utils/formUtils";

import ACButton from "ac-components/ac-button/ACButton";
import CloseIcon from "ac-components/ac-icons/CloseIcon";

import { getFieldWithFindingVariablesSelect } from "../genericIntegrationTicketUtils";
import { InitialValues } from "./types";

type FieldValue = {
  [name: string]: any;
};

type Option = {
  allowedValues: string[];
  children: ReactElement;
  id: string;
  name: string;
};

export type CustomFieldV2 = {
  name: string;
  description?: string;
  placeholder: string;
  options: Option[];
  props: any;
  requireField: boolean;
  isMapped: boolean;
  rules: Rule[];
  title: string;
  type: string;
};

export type CustomFieldMap = {
  [key: string]: CustomFieldV2;
};

type Props = {
  customFields: CustomFieldV2[];
  form: FormInstance;
  onValuesChange: (value: Record<string, string | undefined>) => void;
  customFieldsValues?: any;
  isFindingTicketPage?: boolean;
  showFieldHeaders?: boolean;
  toolName?: string;
  runbook?: boolean;
  customFieldsValueRef: MutableRefObject<Map<string, any>>;
  initialValues: InitialValues;
};

export function CustomFieldsV2({
  customFields,
  form,
  onValuesChange,
  customFieldsValues,
  isFindingTicketPage = false,
  showFieldHeaders = false,
  toolName,
  runbook = false,
  customFieldsValueRef,
  initialValues,
}: Props) {
  const { UNIFIED_TICKET_TEMPLATE, TICKETING_CUSTOM_SEVERITY_RISK_RATING } =
    useFeatureFlag();
  const [selectedFields, setSelectedFields] = useState<string[]>([]);

  // had to keep this state just to re-render fields to allow dynamic switch b/w calandar and text input
  const [selectedFieldsValues, setSelectedFieldsValues] = useState<FieldValue>(
    customFieldsValues || {},
  );
  const mandatoryFields = useRef<CustomFieldV2[]>([]);
  const nonMandatoryFields = useRef<CustomFieldV2[]>([]);

  const modifiedFields = useMemo(() => {
    return _.map(customFields, (field) => ({
      ...field,
      props: {
        ...(field.props || {}),
        onChange: (value: any) => {
          setSelectedFieldsValues((current) => ({
            ...current,
            [field.name]: value,
          }));
          field.props?.onChange?.(value);
        },
      },
    }));
  }, [customFields]);

  useEffect(() => {
    mandatoryFields.current = [];
    nonMandatoryFields.current = [];
    const alreadySetFields: CustomFieldV2[] = [];

    _.forEach(customFields, (field) => {
      if (field.requireField) {
        mandatoryFields.current.push(field);
      } else {
        nonMandatoryFields.current.push(field);
      }

      // If inside runbook and customfield initialvalues are not empty (i.e. in edit mode) then do not render fields without the initial values.
      const isMappedInRunbook =
        runbook &&
        !isEmpty(initialValues?.customFields) &&
        _.find(initialValues.customFields, { key: field.name, active: true });

      if (
        (!runbook && !field.requireField && field.isMapped) ||
        isMappedInRunbook
      ) {
        alreadySetFields.push(field);
      }
    });

    const newFields = [...mandatoryFields.current, ...alreadySetFields].map(
      (field: CustomFieldV2) => field.name,
    );
    setSelectedFields((currentFields) =>
      _.uniq([...currentFields, ...newFields]),
    );
  }, [customFields, initialValues.customFields, runbook]);

  return (
    <div className="custom-fields-v2-wrapper">
      {showFieldHeaders && (
        <Row className={`jira-custom-field custom-field-header`}>
          <Col span={6}>
            <span>Ticket Field</span>
          </Col>
          <Col span={6}>
            <span>Value</span>
          </Col>
          <Col span={2} style={{ margin: 0 }}>
            <span>Enable?</span>
          </Col>
        </Row>
      )}
      {selectedFields.map((_field: string, i: number) => {
        const field = modifiedFields.find((entry) => entry.name === _field);

        const fieldWithFindingVariableSelect = field
          ? getFieldWithFindingVariablesSelect({
              field,
              form,
              onValuesChange: (value: any) => {
                setSelectedFieldsValues((current) => ({
                  ...current,
                  ...value,
                }));
                onValuesChange?.(value);
              },
              customFieldsValues: selectedFieldsValues,
              isFindingTicketPage,
              toolName,
            })
          : undefined;
        // The $fieldWithFindingVariableSelect function returns an array.
        // Now, it is necessary to display severity and priority fields
        // below the custom field.
        //
        // To achieve this:
        // - Place the severity and priority fields in a separate row
        //   after the custom field and the switch.
        // - This ensures that the existing rows and columns for the
        //   custom field remain undisturbed.
        const customFieldwithVariableInputWithSwitch =
          !isEmpty(fieldWithFindingVariableSelect) &&
          fieldWithFindingVariableSelect?.[0];
        const severityMappingGroupFields =
          !isEmpty(fieldWithFindingVariableSelect) &&
          fieldWithFindingVariableSelect?.[1];

        return (
          <>
            <div
              className={classNames("jira-custom-field_v2", {
                [`custom-field-${i}`]: !showFieldHeaders || i !== 0,
              })}
              key={`custom-field-${i}`}
            >
              <Row
                className={classNames("d-flex align-items-center w-100", {
                  "m-t-xs": i !== 0,
                })}
              >
                <Col
                  span={
                    runbook
                      ? field?.name === "parent"
                        ? 22
                        : 24
                      : field?.name === "parent"
                        ? 11
                        : 12
                  }
                  className={`p-x-xs ${runbook ? "p-b-xs" : ""}`}
                >
                  {/** @ts-expect-error: Custom select is not type safe */}
                  <CustomSelect
                    disabled={field?.requireField}
                    value={_field}
                    onChange={(value: string) =>
                      setSelectedFields((fields) => {
                        const _fields = [...fields];
                        _fields[i] = value;
                        return _fields;
                      })
                    }
                    popupMatchSelectWidth={false}
                    placeholder="Search and select field"
                    options={
                      field?.requireField
                        ? mandatoryFields.current
                        : _.filter(
                            nonMandatoryFields.current,
                            (f) =>
                              f.name === field?.name ||
                              !selectedFields.includes(f.name),
                          )
                    }
                    style={{ width: "100%" }}
                    showSearch
                    optionsMutator={(options: CustomFieldV2[]) =>
                      _.map(options, (entry) => ({
                        value: entry.name,
                        label: entry.title,
                      }))
                    }
                  />
                </Col>
                {field?.name === "parent" && (
                  <Col span={runbook ? 2 : 1}>
                    <Tooltip title="Please provide ticket key only.">
                      <InfoIcon />
                    </Tooltip>
                  </Col>
                )}
                <Col span={runbook ? 2 : 1} className="p-r-xs">
                  <RightArrowIcon />
                </Col>
                <Col span={runbook ? 20 : 10}>
                  {UNIFIED_TICKET_TEMPLATE
                    ? getField(customFieldwithVariableInputWithSwitch, i, 24)
                    : getField(field, i, 24)}
                </Col>
                <Col span={1}>
                  <ACButton
                    iconOnly
                    disabled={field?.requireField}
                    size="sm"
                    type="ghost"
                    className="m-l-xs"
                    onClick={() => {
                      setSelectedFields((fields) => {
                        const _fields = [...fields];
                        const deleted = _fields.splice(i, 1);
                        if (customFieldsValueRef) {
                          customFieldsValueRef.current = {
                            ...customFieldsValueRef.current,
                            [deleted[0]]: undefined,
                          };
                        }
                        return _fields;
                      });
                    }}
                  >
                    <CloseIcon />
                  </ACButton>
                </Col>
              </Row>

              {field?.description && (
                <Row className="description">{field.description}</Row>
              )}
              {severityMappingGroupFields &&
                TICKETING_CUSTOM_SEVERITY_RISK_RATING && (
                  <Row className={`jira-custom-field-severity-field`}>
                    <Col span={24}>
                      {getField(severityMappingGroupFields, i)}
                    </Col>
                  </Row>
                )}
            </div>
          </>
        );
      })}
      <ACButton
        className="m-y-sm m-l-l"
        type="outline"
        size="sm"
        leftIcon={<PlusIcon />}
        onClick={() => setSelectedFields((fields) => [...fields, ""])}
      >
        Add Field Mapping
      </ACButton>
    </div>
  );
}
